import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { taxAPI, formatCurrency, formatPercent } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const TipsCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [tips, setTips] = useState("15000");
  const [magi, setMagi] = useState("60000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.tips({
        annual_tips: Number(tips),
        filing_status: filing,
        magi: Number(magi),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="tips-filing" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Annual Tips Received</label>
        <input
          type="number"
          value={tips}
          onChange={(e) => setTips(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-tips"
        />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Modified AGI (MAGI)</label>
        <input
          type="number"
          value={magi}
          onChange={(e) => setMagi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-magi"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate Tips Deduction"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="tips-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Your OBBBA Tips Deduction</span>
        <h3 className="font-heading text-3xl md:text-4xl font-bold leading-tight mb-2">
          You can deduct <span className="text-primary">{formatCurrency(result.eligible_deduction)}</span> of qualified tips.
        </h3>
        <p className="font-body text-sm text-background/80 mt-3">
          Estimated federal tax savings: <span className="font-mono font-semibold">{formatCurrency(result.estimated_federal_tax_savings)}</span> at your {formatPercent(result.marginal_rate_used, 0)} marginal rate.
        </p>
      </div>
      <div className="bg-card border-2 border-foreground overflow-hidden">
        <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Statutory Max</span>
          <span className="font-mono tabular-nums text-right">{formatCurrency(result.max_deduction)}</span>
        </div>
        <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Phase-Out Starts</span>
          <span className="font-mono tabular-nums text-right">{formatCurrency(result.phaseout_threshold)} MAGI</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Phase-Out Applied</span>
          <span className={`font-mono text-right font-semibold ${result.phaseout_applied ? "text-destructive" : "text-primary"}`}>
            {result.phaseout_applied ? "Yes" : "No"}
          </span>
        </div>
      </div>
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.note}</p>
      </div>
      <EmailGatePdfButton
        testid="tips-pdf"
        filename="taxshift-2026-tips.pdf"
        pdfPayload={{
          title: "OBBBA No Tax on Tips Analysis",
          subtitle: `${result.inputs.filing_status.replace("_", " ").toUpperCase()} filer`,
          inputs: [
            { label: "Annual Tips", value: formatCurrency(result.inputs.annual_tips) },
            { label: "MAGI", value: formatCurrency(result.inputs.magi) },
            { label: "Filing Status", value: result.inputs.filing_status.replace("_", " ").toUpperCase() },
          ],
          headline: `You can deduct ${formatCurrency(result.eligible_deduction)} of tips — estimated federal tax savings ${formatCurrency(result.estimated_federal_tax_savings)}.`,
          rows: [
            { label: "Max Deduction", v2025: formatCurrency(result.max_deduction), v2026: formatCurrency(result.max_deduction) },
            { label: "Phase-Out Threshold", v2025: formatCurrency(result.phaseout_threshold), v2026: formatCurrency(result.phaseout_threshold) },
            { label: "Eligible Deduction", v2025: formatCurrency(result.eligible_deduction), v2026: formatCurrency(result.eligible_deduction) },
            { label: "Estimated Tax Savings", v2025: formatCurrency(result.estimated_federal_tax_savings), v2026: formatCurrency(result.estimated_federal_tax_savings) },
          ],
        }}
      />
      <ResultExplainer slug="no-tax-on-tips" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="tips-empty">
      <p className="font-heading text-xl">Enter your tips income and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="No Tax on Tips Calculator (OBBBA 2026)"
      slug="no-tax-on-tips"
      overline="Calculator · OBBBA · Tips"
      description="The One Big Beautiful Bill Act created a new deduction for up to $25,000 of qualified tips income, available for tax years 2025-2028. This calculator shows your eligible deduction and estimated federal tax savings."
      seoTitle="No Tax on Tips Calculator 2026 — OBBBA $25k Deduction"
      seoKeywords="no tax on tips calculator, tips deduction 2026, obbba tips, $25000 tips deduction, tipped worker tax"
      jsonLd={buildCalculatorSchema({
        name: "No Tax on Tips Calculator (OBBBA)",
        description: "Free calculator for OBBBA's $25,000 tips deduction with MAGI phase-out.",
        url: "/calculator/no-tax-on-tips",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">How the OBBBA tips deduction works</h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA (Public Law 119-21) created a new above-the-line deduction at IRC §224 allowing qualified tipped workers
              to deduct up to <strong>$25,000</strong> of reported cash and charged tips from federal income tax, for tax years
              2025 through 2028. The deduction is available whether you itemize or take the standard deduction.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Who qualifies</h3>
            <p>
              Workers in occupations <em>customarily and regularly</em> receiving tips, as published on an IRS list
              (restaurant servers, bartenders, hotel staff, barbers, delivery drivers, etc.). Self-employed individuals
              providing services in these occupations may also qualify.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Phase-out</h3>
            <p>
              The deduction phases out at a rate of $100 per $1,000 of MAGI above $150,000 (single) or $300,000 (married
              joint). A $25,000 deduction is fully phased out at MAGI of $400,000 (single) / $550,000 (joint).
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Important: payroll taxes still apply</h3>
            <p>
              The deduction applies only to federal income tax. Tips remain subject to Social Security and Medicare (FICA) taxes.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "no-tax-on-overtime", label: "No Tax on Overtime" },
        { slug: "senior-deduction-2026", label: "Senior Deduction" },
      ]}
    />
  );
};

export default TipsCalculator;
